import { useTranslation } from "@sagot/i18n";
import { Button, EmptyState, ErrorState, Icon, Input, Select, TableEmptyRow, TableSkeletonRows, Tabs, TabsContent, TabsList, TabsTrigger } from "@sagot/ui";
import { useState, type ReactNode } from "react";
import type { SourceView } from "../shared/types";
import { useAudienceLabel, useAudiences } from "../shared/use-shared-queries";
import { filterSources, pageOf, PAGE_SIZE, SOURCE_TABS, tabCounts, TYPE_FILTERS, type SourceFilters, type SourceTab, type TypeFilter } from "./source-model";
import { SourceRow, type SourceRowAbilities, type SourceRowActions } from "./source-row";

const COLUMNS = 7;
const K = "deployKnowledge.knowledge";
const INITIAL: SourceFilters = { tab: "all", query: "", type: "", audience: null };

interface SourcesTableProps extends SourceRowActions {
  sources: SourceView[] | undefined;
  isError: boolean;
  onRetry: () => void;
  workspaceSlug: string;
  agentId: string | null;
  can: SourceRowAbilities;
  emptyAction: ReactNode;
}

function FilterBar({ filters, onChange }: { filters: SourceFilters; onChange: (next: SourceFilters) => void }) {
  const { t } = useTranslation();
  const audiences = useAudiences();
  return (
    <div className="flex flex-col gap-2 border-b border-line px-4 py-3 md:flex-row md:items-center">
      <div className="relative flex-1">
        <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
        <Input
          className="h-9 pl-9"
          placeholder={t(`${K}.filter.search`)}
          aria-label={t(`${K}.filter.search`)}
          value={filters.query}
          onChange={(e) => { onChange({ ...filters, query: e.target.value }); }}
        />
      </div>
      <Select
        className="h-9 md:w-44"
        aria-label={t(`${K}.filter.type`)}
        value={filters.type}
        onChange={(e) => { onChange({ ...filters, type: e.target.value as TypeFilter | "" }); }}
      >
        <option value="">{t(`${K}.filter.allTypes`)}</option>
        {TYPE_FILTERS.map((type) => (
          <option key={type} value={type}>
            {t(`${K}.types.${type}`)}
          </option>
        ))}
      </Select>
      <Select
        className="h-9 md:w-44"
        aria-label={t(`${K}.filter.access`)}
        value={filters.audience === null ? "" : String(filters.audience)}
        onChange={(e) => { onChange({ ...filters, audience: e.target.value ? Number(e.target.value) : null }); }}
      >
        <option value="">{t(`${K}.filter.allAccess`)}</option>
        {(audiences.data ?? []).map((audience) => (
          <option key={audience.code} value={audience.code}>
            {audience.label}
          </option>
        ))}
      </Select>
    </div>
  );
}

/** Sources card: type tabs with counts, filters, table, client-side paging. */
export function SourcesTable({ sources, isError, onRetry, workspaceSlug, agentId, can, emptyAction, ...actions }: SourcesTableProps) {
  const { t } = useTranslation();
  const labelOf = useAudienceLabel();
  const [filters, setFilters] = useState<SourceFilters>(INITIAL);
  const [page, setPage] = useState(0);
  const counts = tabCounts(sources ?? []);
  const visible = filterSources(sources ?? [], filters, labelOf);
  const pages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const rows = pageOf(visible, current);
  const update = (next: SourceFilters) => {
    setFilters(next);
    setPage(0);
  };
  const body = () => {
    if (!sources) return <TableSkeletonRows rows={6} columns={COLUMNS} />;
    if (sources.length === 0) {
      return (
        <TableEmptyRow colSpan={COLUMNS}>
          <EmptyState icon="book-open" headingLevel={3} title={t(`${K}.empty.title`)} description={t(`${K}.empty.body`)} action={emptyAction} />
        </TableEmptyRow>
      );
    }
    if (rows.length === 0) {
      const clear = (
        <Button variant="secondary" size="sm" onClick={() => { update(INITIAL); }}>
          {t(`${K}.empty.clearFilters`)}
        </Button>
      );
      return (
        <TableEmptyRow colSpan={COLUMNS}>
          <EmptyState icon="search" headingLevel={3} title={t(`${K}.empty.filteredTitle`)} description={t(`${K}.empty.filteredBody`)} action={clear} />
        </TableEmptyRow>
      );
    }
    return rows.map((source) => <SourceRow key={source.id} source={source} workspaceSlug={workspaceSlug} agentId={agentId} can={can} {...actions} />);
  };
  // The table scroller is `relative`: the table's sr-only caption and Actions label are absolutely
  // positioned and, without a positioned scroll container, escape its clip and widen the whole page.
  return (
    <section className="card overflow-hidden" aria-label={t(`${K}.table.caption`)}>
      <Tabs value={filters.tab} onValueChange={(tab) => { update({ ...filters, tab: tab as SourceTab }); }}>
        <TabsList className="overflow-x-auto px-3" aria-label={t(`${K}.tabs.label`)}>
          {SOURCE_TABS.map((tab) => (
            <TabsTrigger key={tab} value={tab}>
              {t(`${K}.tabs.${tab}`)} <span className="ml-1 text-fg-subtle">{sources ? counts[tab] : ""}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        {SOURCE_TABS.map((tab) => (
          <TabsContent key={tab} value={tab}>
            <FilterBar filters={filters} onChange={update} />
            {isError ? (
              <ErrorState title={t(`${K}.empty.loadError`)} description={t("deployKnowledge.loadErrorBody")} onRetry={onRetry} retryLabel={t("common.retry")} />
            ) : (
              <div className="relative overflow-x-auto">
                <table className="table">
                  <caption className="sr-only">{t(`${K}.table.caption`)}</caption>
                  <thead>
                    <tr>
                      <th>{t(`${K}.table.name`)}</th>
                      <th>{t(`${K}.table.type`)}</th>
                      <th>{t(`${K}.table.access`)}</th>
                      <th>{t(`${K}.table.status`)}</th>
                      <th className="text-right">{t(`${K}.table.chunks`)}</th>
                      <th>{t(`${K}.table.updated`)}</th>
                      <th className="w-10">
                        <span className="sr-only">{t(`${K}.table.actions`)}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>{body()}</tbody>
                </table>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
      <div className="flex items-center justify-between border-t border-line px-4 py-3 text-xs text-fg-subtle">
        <span aria-live="polite">{sources ? t(`${K}.table.showing`, { shown: rows.length, total: sources.length }) : ""}</span>
        <div className="flex gap-1.5">
          <Button variant="secondary" size="sm" disabled={current === 0} onClick={() => { setPage(current - 1); }}>
            {t(`${K}.table.previous`)}
          </Button>
          <Button variant="secondary" size="sm" disabled={current >= pages - 1} onClick={() => { setPage(current + 1); }}>
            {t(`${K}.table.next`)}
          </Button>
        </div>
      </div>
    </section>
  );
}
